export type BuilderSection = {
  id: string;
  type: string;
  variant: string;
  enabled: boolean;
  settings: Record<string, unknown>;
};

export type SectionDefinition = {
  type: string;
  label: string;
  description: string;
  category: "Utama" | "Konten" | "Kepercayaan" | "Konversi" | "Ekspor";
  variants: Array<{ id: string; label: string; description: string }>;
  defaultSettings: Record<string, unknown>;
  exportOnly?: boolean;
};

export const sectionCatalog: SectionDefinition[] = [
  {
    type: "hero",
    label: "Hero",
    description: "Judul utama, foto produk, dan tombol tindakan pertama.",
    category: "Utama",
    variants: [
      { id: "split", label: "Split visual", description: "Teks dan foto berdampingan." },
      { id: "centered", label: "Centered", description: "Pesan utama besar dan fokus." },
      { id: "editorial", label: "Editorial", description: "Komposisi premium seperti majalah." },
    ],
    defaultSettings: {
      kicker: "Produk lokal pilihan",
      headline: "Produk berkualitas yang layak dikenal lebih luas",
      subheadline: "Dibuat dengan perhatian pada mutu, detail, dan pelayanan yang hangat.",
      ctaText: "Lihat Produk",
    },
  },
  {
    type: "about",
    label: "Tentang Usaha",
    description: "Cerita singkat usaha, nilai, dan keunikan produk.",
    category: "Konten",
    variants: [
      { id: "story", label: "Brand story", description: "Cerita dengan angka pendukung." },
      { id: "split", label: "Foto + cerita", description: "Visual besar dengan narasi." },
      { id: "compact", label: "Ringkas", description: "Cocok untuk halaman produk singkat." },
    ],
    defaultSettings: {
      kicker: "Cerita kami",
      headline: "Tumbuh dari ketekunan, dipercaya karena kualitas",
      subheadline: "Kami membantu pelanggan menemukan produk lokal yang dibuat dengan proses yang jujur dan bertanggung jawab.",
      statOneValue: "5+",
      statOneLabel: "Tahun berkarya",
      statTwoValue: "1.200+",
      statTwoLabel: "Pelanggan",
    },
  },
  {
    type: "products",
    label: "Produk",
    description: "Katalog produk yang tersinkron dengan data produk dashboard.",
    category: "Utama",
    variants: [
      { id: "grid", label: "Grid katalog", description: "Beberapa produk dalam kartu." },
      { id: "single-focus", label: "Produk unggulan", description: "Satu produk dengan fokus besar." },
      { id: "horizontal", label: "Horizontal", description: "Kartu lebar untuk detail produk." },
    ],
    defaultSettings: {
      kicker: "Katalog",
      headline: "Produk pilihan kami",
      subheadline: "Pilih produk yang paling sesuai dengan kebutuhanmu.",
    },
  },
  {
    type: "benefits",
    label: "Keunggulan",
    description: "Alasan pelanggan memilih dan mempercayai usaha Anda.",
    category: "Kepercayaan",
    variants: [
      { id: "cards", label: "Kartu", description: "Empat keunggulan dalam kartu." },
      { id: "minimal", label: "Minimal", description: "Ringkas dan elegan." },
      { id: "band", label: "Highlight band", description: "Kontras kuat untuk nilai utama." },
    ],
    defaultSettings: {
      kicker: "Kenapa kami",
      headline: "Lebih mudah memilih dengan informasi yang jelas",
      subheadline: "Kualitas produk, proses pemesanan, dan layanan disampaikan secara transparan.",
    },
  },
  {
    type: "stats",
    label: "Angka Kepercayaan",
    description: "Tampilkan angka penting seperti pelanggan, kapasitas, atau pengalaman.",
    category: "Kepercayaan",
    variants: [
      { id: "strip", label: "Metric strip", description: "Empat angka dalam satu baris." },
      { id: "cards", label: "Stat cards", description: "Angka dalam kartu terpisah." },
    ],
    defaultSettings: {
      kicker: "Dipercaya pelanggan",
      headline: "Bukti yang tumbuh bersama usaha",
      itemOneValue: "1.200+",
      itemOneLabel: "Pelanggan",
      itemTwoValue: "98%",
      itemTwoLabel: "Pesanan selesai",
      itemThreeValue: "4,9/5",
      itemThreeLabel: "Rating",
      itemFourValue: "24 jam",
      itemFourLabel: "Respons maksimal",
    },
  },
  {
    type: "process",
    label: "Cara Pesan",
    description: "Jelaskan alur pemesanan secara singkat dan mudah dipahami.",
    category: "Konversi",
    variants: [
      { id: "steps", label: "Langkah", description: "Tiga langkah bernomor." },
      { id: "timeline", label: "Timeline", description: "Alur horizontal interaktif." },
    ],
    defaultSettings: {
      kicker: "Mudah dan cepat",
      headline: "Tiga langkah sampai pesanan diproses",
      stepOneTitle: "Pilih produk",
      stepOneText: "Tentukan produk dan jumlah yang dibutuhkan.",
      stepTwoTitle: "Konfirmasi detail",
      stepTwoText: "Kirim alamat dan kebutuhan melalui WhatsApp.",
      stepThreeTitle: "Bayar dan proses",
      stepThreeText: "Bayar setelah total dikonfirmasi penjual.",
    },
  },
  {
    type: "gallery",
    label: "Galeri",
    description: "Tampilkan produk, proses produksi, atau suasana usaha.",
    category: "Konten",
    variants: [
      { id: "masonry", label: "Masonry", description: "Komposisi foto dinamis." },
      { id: "grid", label: "Grid", description: "Susunan foto yang rapi." },
    ],
    defaultSettings: {
      kicker: "Di balik produk",
      headline: "Galeri produk dan proses",
      subheadline: "Lihat detail, proses, dan karakter produk kami.",
    },
  },
  {
    type: "testimonials",
    label: "Testimoni",
    description: "Bangun kepercayaan lewat pengalaman pelanggan.",
    category: "Kepercayaan",
    variants: [
      { id: "cards", label: "Kartu", description: "Tiga testimoni sekaligus." },
      { id: "spotlight", label: "Spotlight", description: "Satu kutipan utama yang kuat." },
    ],
    defaultSettings: {
      kicker: "Kata pelanggan",
      headline: "Pengalaman yang membuat pelanggan kembali",
      subheadline: "Testimoni dapat diganti dengan ulasan asli usaha Anda.",
    },
  },
  {
    type: "faq",
    label: "FAQ",
    description: "Jawab keraguan yang sering muncul sebelum pelanggan membeli.",
    category: "Konversi",
    variants: [
      { id: "accordion", label: "Accordion", description: "Pertanyaan dapat dibuka-tutup." },
      { id: "two-column", label: "Dua kolom", description: "Lebih padat untuk desktop." },
    ],
    defaultSettings: {
      kicker: "Pertanyaan umum",
      headline: "Hal yang sering ditanyakan",
      subheadline: "Informasi penting sebelum pelanggan melakukan pemesanan.",
    },
  },
  {
    type: "cta",
    label: "CTA WhatsApp",
    description: "Ajakan kuat untuk menghubungi atau memesan melalui WhatsApp.",
    category: "Konversi",
    variants: [
      { id: "band", label: "Full band", description: "Bidang lebar dengan kontras tinggi." },
      { id: "card", label: "Floating card", description: "Kartu CTA yang ringan." },
    ],
    defaultSettings: {
      kicker: "Siap memesan?",
      headline: "Diskusikan kebutuhanmu langsung dengan kami",
      subheadline: "Kami akan membantu mengecek stok, ongkir, dan estimasi proses.",
      ctaText: "Chat WhatsApp",
    },
  },
  {
    type: "export-profile",
    label: "Profil Ekspor",
    description: "MOQ, kapasitas, lead time, private label, dan inquiry buyer.",
    category: "Ekspor",
    exportOnly: true,
    variants: [
      { id: "spec-grid", label: "Spec grid", description: "Spesifikasi B2B dalam grid." },
      { id: "catalog", label: "Catalog sheet", description: "Tampilan seperti lembar katalog." },
    ],
    defaultSettings: {
      kicker: "B2B information",
      headline: "Export product specifications",
      subheadline: "Informasi utama untuk evaluasi buyer dan sourcing partner.",
      hsCode: "4602",
      moq: "100 units",
      capacity: "5,000 units/month",
      leadTime: "21–30 days",
      packaging: "Export carton + moisture protection",
      privateLabel: "Available",
      incoterm: "FOB / EXW",
      language: "English",
      targetMarket: "Global buyers",
    },
  },
  {
    type: "footer",
    label: "Footer",
    description: "Identitas usaha, navigasi, dan kontak di bagian bawah.",
    category: "Utama",
    variants: [
      { id: "default", label: "Default", description: "Footer lengkap tiga kolom." },
      { id: "compact", label: "Compact", description: "Footer ringkas satu baris." },
    ],
    defaultSettings: {},
  },
];

export type TemplatePreset = {
  id: string;
  name: string;
  eyebrow: string;
  description: string;
  themeId: "culinary-fresh" | "craft-natural" | "export-professional";
  accent: string;
  exportOnly?: boolean;
  sections: Array<Omit<BuilderSection, "id">>;
};

const section = (type: string, variant: string, settings: Record<string, unknown> = {}): Omit<BuilderSection, "id"> => ({
  type,
  variant,
  enabled: true,
  settings: {
    ...(sectionCatalog.find((item) => item.type === type)?.defaultSettings || {}),
    ...settings,
  },
});

export const builderTemplates: TemplatePreset[] = [
  {
    id: "jualan-cepat",
    name: "Jualan Cepat",
    eyebrow: "Fokus konversi",
    description: "Hero kuat, produk unggulan, cara pesan, dan CTA WhatsApp.",
    themeId: "culinary-fresh",
    accent: "from-orange-400 to-rose-500",
    sections: [
      section("hero", "split"),
      section("products", "single-focus"),
      section("benefits", "cards"),
      section("process", "steps"),
      section("faq", "accordion"),
      section("cta", "band"),
      section("footer", "default"),
    ],
  },
  {
    id: "brand-story",
    name: "Brand Story",
    eyebrow: "Cerita & kepercayaan",
    description: "Cocok untuk craft, fashion, dan produk bernilai cerita.",
    themeId: "craft-natural",
    accent: "from-emerald-500 to-teal-600",
    sections: [
      section("hero", "editorial"),
      section("about", "story"),
      section("stats", "strip"),
      section("products", "grid"),
      section("gallery", "masonry"),
      section("testimonials", "spotlight"),
      section("cta", "card"),
      section("footer", "default"),
    ],
  },
  {
    id: "catalog-clean",
    name: "Catalog Clean",
    eyebrow: "Produk lebih dominan",
    description: "Tampilan minimal untuk katalog dengan banyak produk.",
    themeId: "craft-natural",
    accent: "from-slate-600 to-slate-900",
    sections: [
      section("hero", "centered"),
      section("products", "grid"),
      section("benefits", "minimal"),
      section("testimonials", "cards"),
      section("faq", "two-column"),
      section("cta", "band"),
      section("footer", "compact"),
    ],
  },
  {
    id: "export-b2b",
    name: "Export B2B",
    eyebrow: "Buyer-ready",
    description: "Company profile bilingual dengan spesifikasi dan inquiry.",
    themeId: "export-professional",
    accent: "from-cyan-500 to-blue-700",
    exportOnly: true,
    sections: [
      section("hero", "editorial", { kicker: "Export-ready business", ctaText: "View Catalog" }),
      section("about", "split", { headline: "Indonesian craftsmanship for global partners" }),
      section("stats", "cards", { itemOneValue: "15+", itemOneLabel: "Product SKUs", itemTwoValue: "5K", itemTwoLabel: "Monthly capacity", itemThreeValue: "30 days", itemThreeLabel: "Lead time", itemFourValue: "B2B", itemFourLabel: "Private label" }),
      section("products", "horizontal", { headline: "Export product catalog" }),
      section("export-profile", "spec-grid"),
      section("cta", "band", { headline: "Let’s discuss your sourcing needs", ctaText: "Send Inquiry" }),
      section("footer", "default"),
    ],
  },
];
